"use client"

import { useState, useEffect } from "react"
import { db, auth } from "../firebaseconfig"
import { collection, getDocs, updateDoc, doc, setDoc } from "firebase/firestore"
import { createUserWithEmailAndPassword } from "firebase/auth"
import "../styles/userManagement.css" // Asegúrate de tener este archivo CSS para estilos personalizados

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [userSearchTerm, setUserSearchTerm] = useState("")
  const [selectedUsers, setSelectedUsers] = useState([])
  const [users, setUsers] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    rol: "user"
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"))
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      setUsers(usersData)
    } catch (error) {
      console.error("Error al obtener usuarios:", error)
    }
  }

  const filteredUsers = users.filter(
    (user) =>
      (user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.rol?.toLowerCase().includes(userSearchTerm.toLowerCase()))
  )

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const handleSelectAll = () => {
    setSelectedUsers(selectedUsers.length === filteredUsers.length ? [] : filteredUsers.map((user) => user.id))
  }

  const handleRoleChange = async (userId, newRole) => {
    try {
      const userRef = doc(db, "users", userId)
      await updateDoc(userRef, { rol: newRole })

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, rol: newRole } : user
        )
      )

      console.log(`Rol del usuario ${userId} actualizado a ${newRole}`)
    } catch (error) {
      console.error("Error al actualizar rol:", error)
    }
  }

  const createUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
      alert("❌ Por favor complete todos los campos requeridos")
      return
    }

    if (newUser.password.length < 6) {
      alert("❌ La contraseña debe tener al menos 6 caracteres")
      return
    }

    setLoading(true)
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password)
      const user = userCredential.user

      // Crear documento en Firestore
      await setDoc(doc(db, "users", user.uid), {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        rol: newUser.rol,
        status: "Activo",
        avatar: newUser.firstName.charAt(0).toUpperCase() + newUser.lastName.charAt(0).toUpperCase(),
        fechaCreacion: new Date().toISOString()
      })

      // Actualizar lista de usuarios
      await fetchUsers()

      // Limpiar formulario y cerrar modal
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        rol: "user"
      })
      setShowCreateModal(false)

      alert("✅ Usuario creado exitosamente")
      console.log("Usuario creado:", user.uid)

    } catch (error) {
      console.error("Error creando usuario:", error)
      let errorMessage = "Error al crear el usuario"
      
      if (error.code === "auth/email-already-in-use") {
        errorMessage = "Este email ya está registrado"
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Email inválido"
      } else if (error.code === "auth/weak-password") {
        errorMessage = "La contraseña es muy débil"
      }
      
      alert(`❌ ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const RoleSelector = ({ user }) => (
    <select
      value={user.rol}
      onChange={(e) => handleRoleChange(user.id, e.target.value)}
      className="role-selector"
    >
      <option value="concesionaria">Concesionaria</option>
      <option value="user">User</option>
      <option value="admin">Admin</option>
    </select>
  )

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>Usuarios</h2>
        <p>Gestiona los usuarios administradores del sistema.</p>
        <button 
          className="create-user-button"
          onClick={() => setShowCreateModal(true)}
          style={{
            background: "#003226",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
            marginTop: "10px"
          }}
        >
          + Crear Usuario
        </button>
      </div>

      <div className="user-management-card">
        <div className="card-header">
          <h3>Usuarios administradores</h3>
          <p>Lista de usuarios con acceso al panel de administración.</p>
        </div>

        <div className="table-controls">
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por nombre, email o rol..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </td>
                  <td>
                    <div className="user-info">
                      <div className="avatar-large">{user.avatar}</div>
                      <div>
                        <div className="user-name">{user.firstName}</div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${user.rol.toLowerCase()}`}>{user.rol}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status === "Activo" ? "active" : "inactive"}`}>{user.status}</span>
                  </td>
                  <td>
                    <RoleSelector user={user} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear usuario */}
      {showCreateModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "white",
            padding: "30px",
            borderRadius: "15px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflow: "auto"
          }}>
            <h2 style={{ marginTop: 0, color: "#003226" }}>Crear Nuevo Usuario</h2>
            
            <div style={{ display: "grid", gap: "15px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Nombre *
                </label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Ingrese el nombre"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    fontSize: "16px"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Apellido *
                </label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Ingrese el apellido"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    fontSize: "16px"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="usuario@ejemplo.com"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    fontSize: "16px"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Contraseña *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    fontSize: "16px"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Rol *
                </label>
                <select
                  value={newUser.rol}
                  onChange={(e) => setNewUser(prev => ({ ...prev, rol: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    fontSize: "16px"
                  }}
                >
                  <option value="user">User</option>
                  <option value="concesionaria">Concesionaria</option>
                  <option value="agencia">Agencia</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button 
                onClick={createUser}
                disabled={loading}
                style={{
                  flex: 1,
                  background: loading ? "#cccccc" : "#003226",
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                {loading ? "Creando..." : "Crear Usuario"}
              </button>
              <button 
                onClick={() => {
                  setShowCreateModal(false)
                  setNewUser({
                    firstName: "",
                    lastName: "",
                    email: "",
                    password: "",
                    rol: "user"
                  })
                }}
                disabled={loading}
                style={{
                  flex: 1,
                  background: "#666",
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
